import {
  HalfFloatType,
  Mesh,
  OrthographicCamera,
  PerspectiveCamera,
  PlaneGeometry,
  Raycaster,
  RepeatWrapping,
  Scene,
  ShaderMaterial,
  Texture,
  TextureLoader,
  UniformsUtils,
  Vector2,
  WebGLRenderTarget,
} from "three";

import {
  BokehShader,
  BokehDepthShader,
} from "three/examples/jsm/shaders/BokehShader2.js";

import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer.js";
import { OutputPass } from "three/examples/jsm/postprocessing/OutputPass.js";
import { OutlinePass } from "three/examples/jsm/postprocessing/OutlinePass.js";
import { RenderPass } from "three/examples/jsm/postprocessing/RenderPass.js";
import { SMAAPass } from "three/examples/jsm/postprocessing/SMAAPass.js";

import { GUI } from "dat.gui";

import { Context } from "../context";
import { Actor } from "../world/actor/actor";

export class PostprocessManager {
  composer?: EffectComposer;

  private renderPass?: RenderPass;
  private outlinePass?: OutlinePass;
  private smaaPass?: SMAAPass;
  private outputPass?: OutputPass;

  params = {
    jsDepthCalculation: true,
    shaderFocus: false,

    distance: 100,

    fstop: 2.2,
    maxblur: 1.0,

    showFocus: false,
    focalDepth: 2.8,
    manualdof: false,
    vignetting: false,
    depthblur: false,

    threshold: 0.5,
    gain: 2.0,
    bias: 0.5,
    fringe: 0.7,

    focalLength: 35,
    noise: true,
    pentagon: false,

    dithering: 0.0001,
  };

  shaderSettings = {
    rings: 3,
    samples: 4,
  };

  postprocessing: any = { enabled: true };

  private outlineInit = false;

  private raycaster = new Raycaster();
  private pointer = new Vector2();

  private contextRef?: Context;

  private actorOutlined: Actor[] = [];

  private materialDepth = new ShaderMaterial({
    uniforms: BokehDepthShader.uniforms,
    vertexShader: BokehDepthShader.vertexShader,
    fragmentShader: BokehDepthShader.fragmentShader,
  });

  constructor() {
    this.raycaster.firstHitOnly = true;
  }

  init(context: Context) {
    const { camera, container, options, renderer, scene } = context;

    if (!container) {
      console.error("no container for post-process to use");
      return;
    }

    this.params.focalLength = camera.getFocalLength();

    this.contextRef = context;

    renderer.autoClear = false;

    this.materialDepth.uniforms["mNear"].value = camera.near;
    this.materialDepth.uniforms["mFar"].value = camera.far;

    const width = container.clientWidth;
    const height = container.clientHeight;
    this.initPostprocessing(width, height);

    container.addEventListener("pointermove", this.onPointerMove);

    this.resize(width, height);

    this.matChanger();

    // outlines
    this.composer = new EffectComposer(renderer);

    this.renderPass = new RenderPass(scene, camera);
    this.composer.addPass(this.renderPass);

    this.outlinePass = new OutlinePass(
      new Vector2(window.innerWidth, window.innerHeight),
      scene,
      camera
    );
    this.composer.addPass(this.outlinePass);

    const textureLoader = new TextureLoader();
    textureLoader.load(
      `${options.assetBase}/images/tri_pattern.jpg`,
      (texture: Texture) => {
        if (this.outlinePass) {
          this.outlinePass.patternTexture = texture;
          texture.wrapS = RepeatWrapping;
          texture.wrapT = RepeatWrapping;
        }
      }
    );

    this.smaaPass = new SMAAPass(
      width * renderer.getPixelRatio(),
      height * renderer.getPixelRatio()
    );
    this.composer.addPass(this.smaaPass);

    this.outputPass = new OutputPass();
    this.composer.addPass(this.outputPass);
  }

  update() {
    const actors = this.contextRef?.world?.actors.getActors();
    if (!actors) return;

    const newActors = [...actors].filter(
      (x) => !this.actorOutlined.includes(x)
    );

    for (const actor of newActors) {
      this.outlinePass?.selectedObjects.push(actor);
    }

    this.actorOutlined.push(...newActors);
  }

  render() {
    if (!this.contextRef) return;

    const { camera, container, renderer, scene, userOptions, world } =
      this.contextRef;

    if (userOptions.values.effects.dofOrOutline === "dof") {
      if (this.params.jsDepthCalculation) {
        const collider = world?.stadium.collider;
        if (collider) {
          this.raycaster.setFromCamera(this.pointer, camera);

          const intersects = this.raycaster.intersectObject(collider.collider);

          const targetDistance =
            intersects.length > 0 ? intersects[0].distance : 1000;

          this.params.distance +=
            (targetDistance - this.params.distance) * 0.03;

          const sdistance = smoothstep(
            camera.near,
            camera.far,
            this.params.distance
          );

          const ldistance = linearize(1 - sdistance, camera);

          this.postprocessing.bokeh_uniforms["focalDepth"].value = ldistance;

          this.params["focalDepth"] = ldistance;
        } else {
          console.error("no ground plane");
        }
      }

      renderer.clear();

      // render scene into texture

      renderer.setRenderTarget(this.postprocessing.rtTextureColor);
      renderer.clear();
      renderer.render(scene, camera);

      // render depth into texture

      scene.overrideMaterial = this.materialDepth;
      renderer.setRenderTarget(this.postprocessing.rtTextureDepth);
      renderer.clear();
      renderer.render(scene, camera);
      scene.overrideMaterial = null;

      // render bokeh composite

      renderer.setRenderTarget(null);
      renderer.render(this.postprocessing.scene, this.postprocessing.camera);
    } else {
      scene.overrideMaterial = null;

      renderer.setRenderTarget(null);
      renderer.clear();

      if (!this.outlineInit) {
        // NB: this is a workaround - in the init function, the textures don't pick up the
        //     right size...
        // TODO: Find a better fix for this
        if (container) {
          const width = container.clientWidth;
          const height = container.clientHeight;
          this.resize(width, height);
        }
        this.outlineInit = true;
      }

      if (userOptions.values.effects.dofOrOutline === "outline") {
        this.composer?.render();
      } else {
        renderer.render(scene, camera);
      }
    }
  }

  resize(width: number, height: number) {
    this.postprocessing.rtTextureDepth.setSize(width, height);
    this.postprocessing.rtTextureColor.setSize(width, height);

    this.postprocessing.bokeh_uniforms["textureWidth"].value = width;
    this.postprocessing.bokeh_uniforms["textureHeight"].value = height;

    this.composer?.setSize(width, height);
  }

  debugUiHook(ui: GUI) {
    ui.add(this.params, "jsDepthCalculation").onChange(this.matChanger);
    ui.add(this.params, "shaderFocus").onChange(this.matChanger);
    ui.add(this.params, "focalDepth", 0.0, 200.0)
      .listen()
      .onChange(this.matChanger);

    ui.add(this.params, "fstop", 0.1, 22, 0.001).onChange(this.matChanger);
    ui.add(this.params, "maxblur", 0.0, 5.0, 0.025).onChange(this.matChanger);

    ui.add(this.params, "showFocus").onChange(this.matChanger);
    ui.add(this.params, "manualdof").onChange(this.matChanger);
    ui.add(this.params, "vignetting").onChange(this.matChanger);

    ui.add(this.params, "depthblur").onChange(this.matChanger);

    ui.add(this.params, "threshold", 0, 1, 0.001).onChange(this.matChanger);
    ui.add(this.params, "gain", 0, 100, 0.001).onChange(this.matChanger);
    ui.add(this.params, "bias", 0, 3, 0.001).onChange(this.matChanger);
    ui.add(this.params, "fringe", 0, 5, 0.001).onChange(this.matChanger);

    ui.add(this.params, "focalLength", 16, 80, 0.001).onChange(this.matChanger);

    ui.add(this.params, "noise").onChange(this.matChanger);

    ui.add(this.params, "dithering", 0, 0.001, 0.0001).onChange(
      this.matChanger
    );

    ui.add(this.params, "pentagon").onChange(this.matChanger);

    ui.add(this.shaderSettings, "rings", 1, 8)
      .step(1)
      .onChange(this.shaderUpdate);
    ui.add(this.shaderSettings, "samples", 1, 13)
      .step(1)
      .onChange(this.shaderUpdate);

    // matChanger();
  }

  private onPointerMove = (event: PointerEvent) => {
    const container = this.contextRef?.renderer.domElement;

    if (!container) return;

    this.pointer.x = (event.offsetX / container.clientWidth) * 2 - 1;
    this.pointer.y = -(event.offsetY / container.clientHeight) * 2 + 1;

    this.postprocessing.bokeh_uniforms["focusCoords"].value.set(
      this.pointer.x,
      this.pointer.y
    );
  };

  private matChanger = () => {
    if (!this.contextRef) return;

    const { camera } = this.contextRef;

    if (this.postprocessing.bokeh_uniforms) {
      for (const e in this.params) {
        if (e in this.postprocessing.bokeh_uniforms) {
          this.postprocessing.bokeh_uniforms[e].value = this.params[e];
        }
      }
    }

    this.postprocessing.bokeh_uniforms["znear"].value = camera.near;
    this.postprocessing.bokeh_uniforms["zfar"].value = camera.far;
    camera.setFocalLength(this.params.focalLength);
  };

  private shaderUpdate = () => {
    this.postprocessing.materialBokeh.defines.RINGS = this.shaderSettings.rings;
    this.postprocessing.materialBokeh.defines.SAMPLES =
      this.shaderSettings.samples;
    this.postprocessing.materialBokeh.needsUpdate = true;
  };

  private initPostprocessing(width: number, height: number) {
    this.postprocessing.scene = new Scene();

    this.postprocessing.camera = new OrthographicCamera(
      width / -2,
      width / 2,
      height / 2,
      height / -2,
      -10000,
      10000
    );
    this.postprocessing.camera.position.z = 100;

    this.postprocessing.scene.add(this.postprocessing.camera);

    this.postprocessing.rtTextureDepth = new WebGLRenderTarget(width, height, {
      type: HalfFloatType,
    });
    this.postprocessing.rtTextureColor = new WebGLRenderTarget(width, height, {
      type: HalfFloatType,
    });

    const bokeh_shader = BokehShader;

    this.postprocessing.bokeh_uniforms = UniformsUtils.clone(
      bokeh_shader.uniforms
    );

    this.postprocessing.bokeh_uniforms["tColor"].value =
      this.postprocessing.rtTextureColor.texture;
    this.postprocessing.bokeh_uniforms["tDepth"].value =
      this.postprocessing.rtTextureDepth.texture;
    this.postprocessing.bokeh_uniforms["textureWidth"].value = width;
    this.postprocessing.bokeh_uniforms["textureHeight"].value = height;

    this.postprocessing.materialBokeh = new ShaderMaterial({
      uniforms: this.postprocessing.bokeh_uniforms,
      vertexShader: bokeh_shader.vertexShader,
      fragmentShader: bokeh_shader.fragmentShader,
      defines: {
        RINGS: this.shaderSettings.rings,
        SAMPLES: this.shaderSettings.samples,
      },
    });

    this.postprocessing.quad = new Mesh(
      new PlaneGeometry(width, height),
      this.postprocessing.materialBokeh
    );
    this.postprocessing.quad.position.z = -500;
    this.postprocessing.scene.add(this.postprocessing.quad);
  }
}

function linearize(depth: number, camera: PerspectiveCamera) {
  const zfar = camera.far;
  const znear = camera.near;
  return (-zfar * znear) / (depth * (zfar - znear) - zfar);
}

function smoothstep(near: number, far: number, depth: number) {
  const x = saturate((depth - near) / (far - near));
  return x * x * (3 - 2 * x);
}

function saturate(x: number) {
  return Math.max(0, Math.min(1, x));
}
